import React, { useEffect, useState } from "react";
import type { IToolbarComponentProps } from "@/types";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Logger } from "@/utils/logger";

import eventBus from "@/utils/eventBus";
import {
  BackIconCircle,
  CogIcon,
  RidersIcon,
  FlagIcon,
  HomeIcon,
  PeopleIcon,
  LearningPathIcon,
  PortfolioIcon,
  ShowcaseIcon,
  PriceIcon,
} from "@/components/shared";
import {
  useEditorState,
  useNotifications,
} from "@/store";
import { idleShutDown } from "@/api";

import { Dropdown, Menu, PopoverComponent, NotificationIcon } from "./shared";
import type { IMenuItem } from "./shared/menu";
import Notification from "./Notification";
import { FS } from "@/singletons/FileSystem";
import * as Sentry from "@sentry/react";
import { SessionError } from "@/utils";
import MaarifLogo from "@/assets/maarif-white.png";

export const Navbar: React.FC<IToolbarComponentProps> = ({ className }) => {
  const { t } = useTranslation("General");
  const [collapsed, setCollapsed] = useState(true);
  const {
    tokens: { express_api_token: accessToken } = {},
    urlParams: { classroomSlug, previousPath } = {},
    sessionUser: { tenantType } = {} as any,
    sessionUser: user,
    ridersUser,
    setTheme,
    setLanguage,
    initAchievements,
    theme,
  } = useEditorState();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [everBeenOpened, setEverBeenOpened] = useState(false);

  const {
    newNotificationTrigger,
    markAsReadAll,
    markAsReadAllUIManipulation,
    unreadNotificationCount,
  } = useNotifications();

  const [, setTerminalStatus] = useState<boolean>(false);

  window.addEventListener("message", (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data.type === "openTerminal") {
        setTerminalStatus(true);
      }

      if (data.type === "closeTerminal") {
        setTerminalStatus(false);
      }
    } catch (error) {
      return;
    }
  });

  const pushRiders = async (
    route?: string,
    shutdown?: boolean,
    msg?: string,
    close?: boolean
  ) => {
    FS.utils.disableLeaveListener();

    try {
      if (shutdown) {
        if (ridersUser && ridersUser.is_superuser && Number(ridersUser.id) !== Number(user?.id)) {
          Logger.log("Don't end someone else's session")
        } else {
          await idleShutDown(accessToken ?? "")
        }
      }
    } catch (e) {
      Sentry.captureException(new SessionError("Failed to end session", e as Error));
    }

    if (close) {
      window.close();
    }

    const isStaging = window?.location?.host?.includes("staging");

    let host = isStaging
      ? import.meta.env.VITE_STAGING_HOST
      : import.meta.env.VITE_HOST || "http://localhost:5173";

    if (theme === "maarif" && typeof host === "string") {
      host = host.replace("riders.ai", "maarif.riders.ai");
    }

    const url = `${host}${route ?? "/dashboard"}${msg ? `?msg=${msg}` : ""}`;

    useEditorState.setState({ isBackButtonPressed: true });
    window.onbeforeunload = null;
    window.location.href = url;
  };

  const goBack = (msg?: string, close?: boolean) => {
    if (previousPath) {
      if (previousPath.includes("/reports") && close) {
        return pushRiders(previousPath, true, msg, true);
      }
      pushRiders(previousPath, true, msg);
    }
  };

  const ProfileMenu = () => {
    return (
      <Menu>
        {([
          {
            title: t("accountSettings"),
            onClick: () => pushRiders("/account/settings", true),
          },

          (!["mebkit"].includes(import.meta.env.VITE_PLATFORM)) ? {
            title: t("language"),
            items: [
              {
                title: "EN",
                onClick: () => {
                  setLanguage("en");
                  initAchievements?.();
                },
              },
              {
                title: "TR",
                onClick: () => {
                  setLanguage("tr");
                  initAchievements?.();
                },
              },
            ],
          } : undefined,
          ["riders"].includes(import.meta.env.VITE_PLATFORM)
            ? {
              title: t("settingsThemeOptions"),
              items: [
                {
                  title: t("settingsDarkTheme"),
                  onClick: () => setTheme("dark"),
                },
                {
                  title: t("settingsLightTheme"),
                  onClick: () => setTheme("light"),
                },
              ],
            }
            : undefined,
        ].filter(Boolean) as IMenuItem[])}
      </Menu>
    );
  };

  useEffect(() => {
    eventBus.on("event-received", (event) => {
      if (event.type === "close-editor") {
        if (event.reason) {
          return goBack("editor-closed", false);
        }
        goBack("editor-closed");
      }
    });
  }, []);

  useEffect(() => {
    if (newNotificationTrigger > 0) {
      setNotificationsOpen(true);
    }
  }, [newNotificationTrigger]);

  useEffect(() => {
    if (!everBeenOpened) {
      setEverBeenOpened(true);
    }
    if (notificationsOpen) {
      markAsReadAll();
    } else {
      if (everBeenOpened) {
        markAsReadAllUIManipulation();
      }
    }
  }, [notificationsOpen]);

  return (
    <div
      className={`h-full ${collapsed ? "w-16" : "w-[190px]"
        } navbar-color flex-shrink-0  rounded-r-[30px] flex flex-col items-center space-y-1 justify-center py-8 px-0 relative pr-2 ${className}`}
    >
      <span
        className="absolute -right-5 top-[calc(50%-3rem)] h-14 w-5 bg-[var(--navbar-collapse-bg,var(--color-primary))] rounded-r-lg flex flex-col items-center justify-center cursor-pointer z-[999] opacity-60"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className="text-2xl text-white font-bold">
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </span>
      </span>
      <span
        onClick={() => pushRiders(undefined, true)}
        className="cursor-pointer mb-10 ml-2"
      >
        {
          theme === "maarif" ? (
            <img src={MaarifLogo} alt="Maarif" className="w-12 h-12 object-contain" />
          ) :
          // vite env
          import.meta.env.VITE_ICON ? (
            <img src={import.meta.env.VITE_ICON} alt="Logo" />
          ) : (
            <RidersIcon width={47} height={47} color="white" />
          )
        }
      </span>

      {/*Module*/}
      <>
        <NavbarIcon
          Icon={HomeIcon}
          label={collapsed ? undefined : t("homePage")}
          active={true}
          height="h-fit"
          activeColor="fill-[#5EDEA7] !text-[#5EDEA7]"
          onClick={() => pushRiders("/dashboard", true)}
        />
      </>

      {(tenantType === "corporate" || user?.cms_info?.is_teacher) && <>
        <NavbarIcon
          Icon={PeopleIcon}
          active={false}
          height="h-fit"
          label={collapsed ? undefined : t("myClassrooms")}
          activeColor="fill-riders-green-dark text-riders-green"
          onClick={() =>
            pushRiders(
              classroomSlug && classroomSlug !== "undefined"
                ? `/dashboard/classrooms/${classroomSlug}`
                : `/dashboard/classrooms`,
              true
            )
          }
        />
      </>}

      {(tenantType === "individual") && <>
        <NavbarIcon
          Icon={LearningPathIcon}
          active={false}
          height="h-fit"
          label={collapsed ? undefined : t("myProgram")}
          activeColor="fill-riders-green-dark text-riders-green"
          onClick={() =>
            pushRiders(
              classroomSlug && classroomSlug !== "undefined"
                ? `/learning-path?classroom=${classroomSlug}`
                : `/learning-path`,
              true
            )
          }
        />
      </>}

      {(tenantType === "individual") && <>
        <NavbarIcon
          Icon={PortfolioIcon}
          active={false}
          height="h-fit"
          label={collapsed ? undefined : t("myPortfolio")}
          activeColor="fill-riders-green-dark text-riders-green"
          onClick={() =>
            pushRiders(
              "/portfolio"
            )
          }
        />
      </>}

      {(tenantType === "individual") && <>
        <NavbarIcon
          Icon={ShowcaseIcon}
          active={false}
          height="h-fit"
          label={collapsed ? undefined : t("showcase")}
          activeColor="fill-riders-green-dark text-riders-green"
          onClick={() =>
            pushRiders(
              "/showcase"
            )
          }
        />
      </>}

      {(["riders"].includes(import.meta.env.VITE_PLATFORM) && (tenantType === "corporate")) && (
        <>
          <NavbarIcon
            Icon={FlagIcon}
            active={false}
            height="h-fit"
            label={collapsed ? undefined : t("competitions")}
            activeColor="fill-riders-green-dark text-riders-green"
            onClick={() => pushRiders(`/competitions`, true)}
          />
        </>
      )}

      {tenantType === "individual" && user?.cms_info?.is_teacher && (
        <>
          <NavbarIcon
            Icon={PriceIcon}
            active={false}
            height="h-fit"
            label={collapsed ? undefined : t("competitions")}
            activeColor="fill-riders-green-dark text-riders-green"
            onClick={() => pushRiders("/competitions")}
          />
        </>
      )}

      <NavbarIcon
        Icon={BackIconCircle}
        height="h-fit"
        label={collapsed ? undefined : t("goBack")}
        onClick={() => goBack(undefined, true)}
        className="bg-[var(--navbar-icon-active-bg)] !text-[var(--navbar-icon-active-text)] hover:bg-[var(--navbar-icon-hover-bg)] hover:!text-white transition-all"
      />


      <div className="grow"></div>

      {/**  // TODO: notifications     */}
      <PopoverComponent
        theme={theme}
        trigger="click"
        arrow="Hide"
        onOpenChange={() => setNotificationsOpen(!notificationsOpen)}
        open={notificationsOpen}
        content={<Notification />}
      >
        <div className="w-full pl-5 relative">
          {unreadNotificationCount > 0 && (
            <div className="absolute bg-rose-500 text-background text-xs font-medium w-4 h-4 rounded-full flex items-center justify-center left-7 top-2">
              {unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}
            </div>
          )}
          <span className="w-full h-fit py-3 space-x-3 flex items-center cursor-pointer">
            <NotificationIcon size={24} />
            {!collapsed && <p>{t("notifications")}</p>}
          </span>
        </div>
      </PopoverComponent>
      <span className="my-1 w-full space-x-3 flex items-center cursor-pointer">
        <Dropdown menu={<ProfileMenu />} position="bottom-0 left-full">
          <NavbarIcon
            Icon={CogIcon}
            height="h-fit"
            activeColor="fill-riders-green-dark text-riders-green"
            label={collapsed ? undefined : t("settings")}
          />
        </Dropdown>
      </span>

      {/*<Dropdown menu={<SettingsMenu handleLanguageChange={handleLanguageChange}/>} position="bottom-0 left-full">*/}
      {/*  <>*/}
      {/*    <ToolbarIcon Icon={IoSettingsOutline} />*/}
      {/*  </>*/}
      {/*</Dropdown>*/}

      {/*<ToolbarIcon Icon={GoMoon} />*/}
      {/*<button*/}
      {/*    className='bg-[#4BBB8A] py-5 px-1 rounded-r-md absolute top-1/2 left-full -translate-y-1/2'*/}
      {/*    onClick={toggleCollapse}*/}
      {/*>*/}
      {/*    <ChevronRight className={cn(collapsed ? '' : 'rotate-180')}/>*/}
      {/*</button>*/}
    </div>
  );
};

export const NavbarIcon: React.FC<any> = ({
  Icon,
  size = "text-2xl",
  className,
  active = false,
  height = "h-10",
  activeColor = "fill-riders-green-dark dark:fill-riders-green",
  _activeBorderColor = "border-vs-light-input dark:border-vs-dark-input",
  alert = 0,
  onClick: handler,
  label,
}) => {
  return (
    <span
      className={classNames(
        active ? `bg-[var(--navbar-icon-active-bg)]` : "",
        height,
        "py-3 pl-5 w-full dark:my-0 cursor-pointer flex items-center relative rounded-r-xl",
        active ? "text-[var(--navbar-icon-active-text)]" : "text-white",
        className
      )}
      onClick={handler}
    >
      <Icon
        className={`${active ? `fill-[var(--navbar-icon-active-text)]` : "fill-white"} ${size}`}
        fill={
          active
            ? `var(--navbar-icon-active-text)`
            : "fill-arduino-green-dark dark:fill-vs-dark-disabled"
        }
        size={24}
      />
      {label && <p className="ml-3 leading-tight">{label}</p>}
      {alert > 0 && (
        <span className="absolute bottom-1.5 right-1.5 text-2xs bg-riders-green rounded-full px-1">
          {alert}
        </span>
      )}
    </span>
  );
};

